import { useState } from "react"
import cn from "classnames"
import { getGlobals, getAllTeammemberItems, getTeamMemberArchive } from "storyblok/api"
import Meta from "components/Meta"
import PageHeader from "components/DynamicComponent/atoms/PageHeader"
import CTABanner from "components/DynamicComponent/atoms/CTABanner"
import TeamMember from "components/TeamMember"
import Chevron from "public/assets/white-chevron-purple-background.svg"
import richText from "utilities/richText"
import StoryblokVisualEditor from "components/StoryblokVisualEditor"

export default function FAQArchive({ teamMembers, titleOptions, story }) {
  const [activeTab, setActiveTab] = useState("All")
  const [open, setOpen] = useState(false)

  let currentTeamMembers = activeTab === "All" ? teamMembers : teamMembers.filter(({ title }) => title === activeTab)

  const Tab = ({ title }) => (
    <button
      className={cn(
        "uppercase font-primary text-navy lg:px-4 lg:bg-white lg:hover:bg-blue-light lg:hover:text-purple transition-all duration-300 text-left mr-8 lg:mr-0",
        {
          "hidden py-4 lg:block": activeTab !== title && !open,
          "py-4": activeTab === title && !open,
          "lg:bg-blue-light lg:text-purple": activeTab === title,
          "py-6": open,
        }
      )}
      onClick={() => {
        setOpen(false)
        setActiveTab(title)
      }}
    >
      {title}
    </button>
  )

  const description =
    "Our physicians focus on providing exceptional care for you with the latest minimally-invasive, research-based and state-of-the-art spinal procedures available"

  return (
    <>
      <Meta
        info={{
          title: "Meet Our Medical Team | Sonoran Spine",
          og_title: "Meet Our Medical Team | Sonoran Spine",
          twitter_title: "Meet Our Medical Team | Sonoran Spine",
          description: description,
          og_description: description,
          twitter_description: description,
        }}
      />
      <main>
        <PageHeader blok={{ heading: "Our Medical Team" }} />

        <StoryblokVisualEditor story={story?.content}>
          <section className="py-12 px-8">
            <div className="max-w-screen-xl mx-auto prose-p:max-w-4xl prose-p:text-navy prose-p:font-light prose-p:leading-7">
              {richText(story?.content?.content)}
            </div>
          </section>
        </StoryblokVisualEditor>

        <section className="px-8">
          <div className="max-w-screen-xl mx-auto pb-12 relative">
            <nav
              className={cn(
                "flex flex-col lg:flex-row text-left lg:justify-between mb-8 -mx-8 pl-8 lg:pl-0 lg:-mx-0 bg-blue-light lg:bg-white sticky lg:static top-0 ",
                {
                  "divide-y-[0.5px] divide-purple lg:divide-y-0": open,
                }
              )}
            >
              <Tab title="All" />
              {titleOptions.map((title) => (
                <Tab title={title} key={title} />
              ))}
              <button
                className="absolute right-0 top-0 w-14 h-14 lg:hidden"
                onClick={() => setOpen(!open)}
                title="Filter Medical Team by Title"
              >
                <Chevron
                  className={cn("rotate-180", {
                    "rotate-0": open,
                  })}
                />
              </button>
            </nav>

            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-8">
              {currentTeamMembers.map((teamMember) => (
                <TeamMember {...teamMember} key={teamMember.full_slug} />
              ))}
            </div>
          </div>
        </section>
      </main>
      <CTABanner />
    </>
  )
}

export async function getStaticProps({ preview = null }) {
  const globals = await getGlobals()
  const { teamMembers, titleOptions } = await getAllTeammemberItems()
  const story = await getTeamMemberArchive(preview)

  return {
    props: {
      globals,
      teamMembers,
      titleOptions,
      story: story ?? null,
    },
  }
}
